import {useState} from 'react';
import {useMutation} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {Tooltip} from 'antd';
import {UPDATE_COMPANY_RECOMMENDATION} from 'graphql/mutations/companyStartups';
import {GET_ALL_COMPANY_RECOMMENDATIONS} from 'graphql/queries/company';
import {toast} from 'react-toastify';
import styles from './recommendation.module.scss';

/**
 * Companies suggested by users, awaiting a decision.
 *
 * Two things this screen has to be honest about. Many suggestions come from
 * accounts that no longer exist, and rendering "Deleted User" in the same
 * weight as a real name made every row look like a person called that. And a
 * suggestion that has already been added is not gone from the list — it comes
 * back with `isAdded`, so the row has to say so rather than offering Add again.
 */

/**
 * The service substitutes a literal "Deleted User" record for suggestions whose
 * author has since been removed, rather than returning null. Taken at face
 * value that renders as a person's name — and right now every one of these
 * suggestions carries it, so the column became sixty-six identical avatars
 * captioned with the same fake name.
 */
const isRemovedAccount = user =>
  !user ||
  (!user.firstName && !user.lastName) ||
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim().toLowerCase() ===
    'deleted user';

const Avatar = ({url, name}) => {
  const [failed, setFailed] = useState(false);

  if (!url || failed)
    return (
      <span className={styles.avatarFallback} aria-hidden="true">
        {(name || '?').trim()[0]?.toUpperCase() ?? '?'}
      </span>
    );

  return (
    <img
      className={styles.avatar}
      src={url}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const ViewCompanyRecommendation = ({data}) => {
  // Tracks which row is mid-flight. The single `creating` flag disabled every
  // Add button at once, so clicking one row greyed out all sixty-six.
  const [pendingId, setPendingId] = useState(null);

  const [updateCompanyRecommendation] = useMutation(
    UPDATE_COMPANY_RECOMMENDATION,
  );

  const handleAdd = id => {
    setPendingId(id);
    updateCompanyRecommendation({
      variables: {recommendationId: id, isAdded: true},
      refetchQueries: [{query: GET_ALL_COMPANY_RECOMMENDATIONS}],
    })
      .then(({data: {updateCompanyRecommendation: response}}) => {
        if (response?.statusCode) toast.error(response.message);
        else toast.success('Added Successfully');
      })
      .catch(error => toast.error(error.message))
      .finally(() => setPendingId(null));
  };

  const items = data ?? [];
  const pending = items.filter(item => !item?.isAdded);

  return (
    <div className={styles.modal}>
      <header className={styles.header}>
        <h2>Company recommendations</h2>
        <p>
          {pending.length
            ? `${pending.length} of ${items.length} still awaiting a decision`
            : 'Everything here has been reviewed'}
        </p>
      </header>

      {items.length === 0 ? (
        <div className={styles.empty}>No one has suggested a company yet.</div>
      ) : (
        <div className={styles.body}>
          <div className={styles.columns}>
            <span>Suggested by</span>
            <span>Company</span>
            <span />
          </div>

          {items.map(value => {
            const removed = isRemovedAccount(value?.user);
            const suggester = [value?.user?.firstName, value?.user?.lastName]
              .filter(Boolean)
              .join(' ')
              .trim();

            return (
              <div key={value.id} className={styles.row}>
                <div className={styles.person}>
                  {removed ? (
                    <span className={styles.unknown}>Account removed</span>
                  ) : (
                    <>
                      <Avatar url={value?.user?.imageUrl} name={suggester} />
                      <span>{suggester}</span>
                    </>
                  )}
                </div>

                {value?.websiteUrl ? (
                  <Tooltip title={value.websiteUrl}>
                    <a
                      className={styles.company}
                      target="_blank"
                      rel="noreferrer"
                      href={value.websiteUrl}>
                      {value?.companyName}
                    </a>
                  </Tooltip>
                ) : (
                  <span className={styles.companyPlain}>
                    {value?.companyName}
                  </span>
                )}

                <div className={styles.action}>
                  {value?.isAdded ? (
                    <span className={styles.added}>Added</span>
                  ) : (
                    <Button
                      label={pendingId === value.id ? 'Adding…' : 'Add'}
                      className={styles.addButton}
                      onClick={() => handleAdd(value.id)}
                      disabled={pendingId === value.id}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ViewCompanyRecommendation;
